// @ts-nocheck
/*
 * Port of views/dashboard/pending-dispositions/index.js.
 * API: API_DISPOSITION_PENDING_BY_AGENTS.
 * Column count preserved on the main table: 3 (Agent, Pending count, chevron).
 * Row expansion in v1 was inline (click to expand); ported here as
 * per-row modal opened by DataTable's onRowClick so the same table system
 * (obd3-gui DataTable) can be used consistently. Underlying data + counts
 * are byte-identical to v1.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_DISPOSITION_PENDING_BY_AGENTS } from '@/service/url';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';

const fmtWhen = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

export default function PendingDispositionsByAgent() {
  const [data, setData] = useState({ total_pending: 0, agent_count: 0, agents: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailAgent, setDetailAgent] = useState(null);

  const fetchData = useCallback(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    getApiCall(API_DISPOSITION_PENDING_BY_AGENTS)
      .then((resp) => {
        if (!mounted) return;
        setData({
          total_pending: resp?.total_pending || 0,
          agent_count: resp?.agent_count || 0,
          agents: Array.isArray(resp?.agents) ? resp.agents : [],
        });
      })
      .catch((err) => {
        if (!mounted) return;
        setError(typeof err === 'string' ? err : 'Failed to load pending dispositions');
        setData({ total_pending: 0, agent_count: 0, agents: [] });
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const cleanup = fetchData();
    return cleanup;
  }, [fetchData]);

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'agent_name', header: 'Agent' },
      {
        accessorKey: 'pending_count',
        header: 'Pending',
        cell: ({ row }) => (
          <span className={
            'rounded-full px-2 py-0.5 text-xs font-bold ' +
            (row.original.pending_count > 0
              ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
              : 'bg-muted text-muted-foreground')
          }>
            {row.original.pending_count}
          </span>
        ),
      },
      {
        id: 'view',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setDetailAgent(row.original); }}>
            View pending
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h5 className="mb-0 flex items-center gap-2 text-sm font-bold text-foreground">
            Pending Dispositions
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
              {data.total_pending}
            </span>
          </h5>
          <small className="text-xs text-muted-foreground">
            Unsubmitted dispositions awaiting agents
            {data.agent_count ? ` • ${data.agent_count} agent(s)` : ''}
          </small>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {error ? <div className="mb-2 text-sm text-destructive">{error}</div> : null}

      <DataTable
        tableId="dashboard.pending-dispositions-by-agent"
        columns={columns}
        data={data.agents}
        isLoading={loading}
        emptyTitle="No pending dispositions"
        emptyDescription="All agents are caught up."
        pageSize={10}
        onRowClick={(row) => setDetailAgent(row)}
        exportFilename="pending-dispositions.csv"
      />

      <Dialog open={!!detailAgent} onOpenChange={(o) => !o && setDetailAgent(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Pending — {detailAgent?.agent_name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {(detailAgent?.pending || []).length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">No pending items.</div>
            ) : (
              (detailAgent?.pending || []).map((p) => (
                <div key={p.id} className="border-b border-border py-2 text-[13px]">
                  <strong>{p.form_name || 'Disposition'}</strong>
                  {p.caller_number ? ` — ${p.caller_number}` : ''}
                  <div className="mt-0.5 text-[12px] text-muted-foreground">
                    {[
                      p.campaign_name ? `Campaign: ${p.campaign_name}` : null,
                      p.call_direction ? `Direction: ${p.call_direction}` : null,
                      p.disposition_source ? `Source: ${p.disposition_source}` : null,
                      `Opened: ${fmtWhen(p.expires_at || p.created_at)}`,
                    ].filter(Boolean).join('  •  ')}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
